import { ChevronLeft, ChevronRight } from 'lucide-react';
import { getInitials, getAvatarColor } from '../../lib/avatar';

export const MONTHS_SHORT = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];

export const formatDate = (value) => (value ? new Date(value).toLocaleString('tr-TR') : '-');

const pad = (n) => String(n).padStart(2, '0');
const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

// "Bugün, 14:18" / "Dün, 16:22" / "19 Eki 2026, 17:21"
export const formatRelative = (value) => {
  if (!value) return 'Hiç girmedi';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  const time = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  const diff = Math.round((startOfDay(new Date()) - startOfDay(d)) / 86400000);
  if (diff === 0) return `Bugün, ${time}`;
  if (diff === 1) return `Dün, ${time}`;
  return `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]} ${d.getFullYear()}, ${time}`;
};

export const displayName = (user) => user?.display_name || user?.email?.split('@')[0] || '';

export const UserAvatar = ({ user, size = 'h-10 w-10', text = 'text-sm' }) => (
  <span
    className={`${size} ${text} shrink-0 rounded-full font-semibold flex items-center justify-center ${
      user?.is_active === false ? 'bg-secondary text-muted-foreground' : getAvatarColor(user?.email)
    }`}
    aria-hidden="true"
  >
    {getInitials(user?.display_name, user?.email)}
  </span>
);

export const StatusDot = ({ active, label }) => (
  <span className="inline-flex items-center gap-2 text-sm">
    <span className={`h-2 w-2 rounded-full ${active ? 'bg-emerald-500' : 'bg-muted-foreground/60'}`} />
    {label}
  </span>
);

export const SectionTitle = ({ title, description, children }) => (
  <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
    <div>
      <h1 className="text-2xl font-semibold">{title}</h1>
      {description && <p className="text-muted-foreground mt-1">{description}</p>}
    </div>
    {children && <div className="flex items-center gap-2">{children}</div>}
  </div>
);

export const Card = ({ className = '', children, ...rest }) => (
  <div className={`rounded-xl border border-border bg-card ${className}`} {...rest}>{children}</div>
);

export const EmptyState = ({ icon: Icon, text }) => (
  <div className="flex flex-col items-center justify-center gap-2 py-14 text-muted-foreground">
    {Icon && <Icon className="h-9 w-9 opacity-50" />}
    <p className="text-sm">{text}</p>
  </div>
);

// 1 2 3 … 28 gibi sayfa numaraları
const pageList = (page, pages) => {
  if (pages <= 5) return Array.from({ length: pages }, (_, i) => i + 1);
  const set = new Set([1, pages, page, page - 1, page + 1].filter((p) => p >= 1 && p <= pages));
  const sorted = [...set].sort((a, b) => a - b);
  const out = [];
  sorted.forEach((p, i) => {
    if (i > 0 && p - sorted[i - 1] > 1) out.push('…');
    out.push(p);
  });
  return out;
};

export const Pagination = ({ page, perPage, total, onPage, noun = 'kayıt' }) => {
  const pages = Math.max(1, Math.ceil(total / perPage));
  const from = total === 0 ? 0 : (page - 1) * perPage + 1;
  const to = Math.min(total, page * perPage);
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-border text-sm text-muted-foreground" data-testid="pagination">
      <span>{from}–{to} / {total.toLocaleString('tr-TR')} {noun}</span>
      <div className="flex items-center gap-1">
        <button className="h-8 w-8 rounded-lg hover:bg-secondary disabled:opacity-40 flex items-center justify-center" disabled={page <= 1} onClick={() => onPage(page - 1)} aria-label="Önceki sayfa">
          <ChevronLeft className="h-4 w-4" />
        </button>
        {pageList(page, pages).map((p, i) => (p === '…' ? (
          <span key={`gap-${i}`} className="px-1">…</span>
        ) : (
          <button
            key={p}
            onClick={() => onPage(p)}
            aria-current={p === page ? 'page' : undefined}
            className={`h-8 min-w-8 px-2 rounded-lg text-sm ${p === page ? 'bg-primary text-primary-foreground font-medium' : 'hover:bg-secondary'}`}
          >
            {p}
          </button>
        )))}
        <button className="h-8 w-8 rounded-lg hover:bg-secondary disabled:opacity-40 flex items-center justify-center" disabled={page >= pages} onClick={() => onPage(page + 1)} aria-label="Sonraki sayfa">
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export const selectClass = 'h-10 rounded-lg border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring/30';
export const AUDIT_LABELS = {
  USER_ENABLED: 'Kullanıcı etkinleştirildi', USER_DISABLED: 'Kullanıcı askıya alındı', USER_UPDATED: 'Kullanıcı güncellendi',
  USER_CREATED: 'Kullanıcı oluşturuldu', USER_DELETED: 'Kullanıcı silindi', PASSWORD_RESET: 'Şifre sıfırlandı',
  SESSIONS_INVALIDATED: 'Oturumlar sonlandırıldı', SESSION_INVALIDATED: 'Oturum sonlandırıldı', TWO_FA_DISABLED: '2FA devre dışı',
  ADMIN_LOGIN: 'Admin girişi',
};
export const auditLabel = (action) => AUDIT_LABELS[action] || action;
